// Servicio de configuración de IA, tarifas de tokens y registro de consumo global en Firestore y local
import { db } from '../firebase';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';

const STORAGE_KEY_CONFIG = 'minimax_ai_config';
const STORAGE_KEY_HISTORY = 'minimax_ai_query_history';

export const DEFAULT_AI_CONFIG = {
  provider: 'minimax', // 'minimax' | 'gemini'
  minimaxApiKey: '',
  minimaxBaseUrl: '/api/minimax', // '/api/minimax' utiliza el proxy local de Vite sin CORS
  minimaxModel: 'MiniMax-M2.5',
  maxTokens: 4000,
  temperature: 0.2,
  // Tarifas en USD por cada 1 Millón de tokens
  inputPricePerMillion: 0.15, // $0.15 USD / 1M tokens entrada
  outputPricePerMillion: 1.10, // $1.10 USD / 1M tokens salida
  copExchangeRate: 4150, // 1 USD = 4,150 COP
};

export const AVAILABLE_MINIMAX_MODELS = [
  { id: 'MiniMax-M2.5', name: 'MiniMax-M2.5 (Recomendado)', description: 'Modelo insignia para razonamiento y estructuración rápida' },
  { id: 'MiniMax-M3', name: 'MiniMax-M3', description: 'Modelo avanzado de alta capacidad con bloques de pensamiento' },
  { id: 'MiniMax-Text-01', name: 'MiniMax-Text-01', description: 'Modelo optimizado para procesamiento de texto extenso' },
  { id: 'abab6.5s-chat', name: 'abab6.5s-chat', description: 'Versión ligera de alta velocidad' }
];

/**
 * Obtiene la configuración actual de IA desde el almacenamiento local inmediato (caché)
 */
export const getAiConfig = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_CONFIG);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_AI_CONFIG, ...parsed };
    }
  } catch (error) {
    console.warn('Error reading AI config from localStorage:', error);
  }
  return { ...DEFAULT_AI_CONFIG };
};

/**
 * Consulta la configuración de IA directamente desde Firestore (Base de Datos)
 * y actualiza el caché local para accesos posteriores.
 */
export const fetchAiConfigFromDb = async () => {
  try {
    const configDocRef = doc(db, 'configuracion', 'ia');
    const docSnap = await getDoc(configDocRef);

    if (docSnap.exists()) {
      const dbData = docSnap.data();
      const mergedConfig = { ...DEFAULT_AI_CONFIG, ...dbData };
      // Actualizar caché local
      localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(mergedConfig));
      return mergedConfig;
    } else {
      // Si aún no existe el documento en Firestore, crearlo con los valores por defecto
      const initialConfig = getAiConfig();
      await setDoc(configDocRef, {
        ...initialConfig,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      return initialConfig;
    }
  } catch (error) {
    console.error('Error al consultar configuración de IA en Firestore:', error);
    // Retornar la configuración local si falla la conexión
    return getAiConfig();
  }
};

/**
 * Guarda la configuración de IA tanto en Firestore como en localStorage
 */
export const saveAiConfigToDb = async (config, userEmail = 'admin') => {
  try {
    const current = getAiConfig();
    const updated = {
      ...current,
      ...config,
      updatedAt: new Date(),
      updatedBy: userEmail,
    };

    // 1. Guardar en Firestore
    const configDocRef = doc(db, 'configuracion', 'ia');
    await setDoc(configDocRef, updated, { merge: true });

    // 2. Guardar en localStorage para acceso síncrono rápido
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(updated));

    return updated;
  } catch (error) {
    console.error('Error al guardar configuración de IA en Firestore:', error);
    // Fallback a localStorage
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(config));
    throw error;
  }
};

/**
 * Guarda la configuración de IA de forma local
 */
export const saveAiConfig = (config) => {
  try {
    const current = getAiConfig();
    const updated = { ...current, ...config };
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(updated));
    return updated;
  } catch (error) {
    console.error('Error saving AI config to localStorage:', error);
    return config;
  }
};

/**
 * Calcula los costos de una consulta en USD y COP
 */
export const calculateTokenCost = (inputTokens = 0, outputTokens = 0, customConfig = null) => {
  const config = customConfig || getAiConfig();
  const inputPrice = Number(config.inputPricePerMillion) || 0;
  const outputPrice = Number(config.outputPricePerMillion) || 0;
  const exchangeRate = Number(config.copExchangeRate) || 4150;

  const inputCostUsd = (inputTokens / 1_000_000) * inputPrice;
  const outputCostUsd = (outputTokens / 1_000_000) * outputPrice;
  const totalUsd = inputCostUsd + outputCostUsd;
  const totalCop = totalUsd * exchangeRate;

  return {
    inputCostUsd,
    outputCostUsd,
    totalUsd,
    totalCop,
    formattedUsd: `$${totalUsd.toFixed(5)}`,
    formattedCop: `$${totalCop.toFixed(2)} COP`,
  };
};

/**
 * Obtiene el historial local de consultas
 */
export const getAiQueryHistory = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_HISTORY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn('Error reading AI query history from localStorage:', error);
  }
  return [];
};

/**
 * Obtiene el historial global de consultas desde Firestore
 */
export const fetchAiQueryHistoryFromDb = async (limitCount = 100) => {
  try {
    const logsRef = collection(db, 'ai_consumption_logs');
    const q = query(logsRef, orderBy('createdAt', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);

    const logs = [];
    snapshot.forEach((docSnap) => {
      logs.push({
        id: docSnap.id,
        ...docSnap.data(),
      });
    });

    if (logs.length > 0) {
      // Sincronizar en caché local
      localStorage.setItem(STORAGE_KEY_HISTORY, JSON.stringify(logs));
      return logs;
    }
  } catch (error) {
    console.warn('No se pudo cargar el historial desde Firestore, usando local:', error);
  }
  return getAiQueryHistory();
};

/**
 * Registra una consulta en el historial (tanto en Firestore como en localStorage)
 */
export const logAiQuery = async (entry, userEmail = null) => {
  try {
    const config = getAiConfig();
    const cost = calculateTokenCost(entry.inputTokens || 0, entry.outputTokens || 0, config);

    const newEntry = {
      id: Date.now() + Math.random().toString(36).substring(2, 6),
      timestamp: new Date().toISOString(),
      createdAt: new Date(),
      queryType: entry.queryType || 'Transcripción',
      fileName: entry.fileName || 'Documento sin nombre',
      provider: entry.provider || config.provider,
      model: entry.model || (config.provider === 'gemini' ? 'gemini-1.5-flash' : config.minimaxModel),
      inputTokens: entry.inputTokens || 0,
      outputTokens: entry.outputTokens || 0,
      totalTokens: (entry.inputTokens || 0) + (entry.outputTokens || 0),
      costUsd: cost.totalUsd,
      costCop: cost.totalCop,
      durationMs: entry.durationMs || 0,
      success: entry.success !== false,
      error: entry.error || null,
      userEmail: userEmail || entry.userEmail || 'usuario',
      thinkingSnippet: entry.thinkingSnippet || null,
    };

    // 1. Guardar en localStorage
    const history = getAiQueryHistory();
    const updatedHistory = [newEntry, ...history].slice(0, 150);
    localStorage.setItem(STORAGE_KEY_HISTORY, JSON.stringify(updatedHistory));

    // 2. Guardar asíncronamente en Firestore en la colección ai_consumption_logs
    try {
      addDoc(collection(db, 'ai_consumption_logs'), newEntry).catch((err) =>
        console.warn('Advertencia al persistir log de IA en Firestore:', err)
      );
    } catch {
      // Ignorar fallo secundario de log
    }

    return newEntry;
  } catch (error) {
    console.error('Error logging AI query:', error);
    return null;
  }
};

/**
 * Limpia el historial de consultas
 */
export const clearAiQueryHistory = () => {
  try {
    localStorage.removeItem(STORAGE_KEY_HISTORY);
    return true;
  } catch (error) {
    console.error('Error clearing AI query history:', error);
    return false;
  }
};

/**
 * Calcula métricas agregadas del consumo de tokens y costos
 */
export const getAiConsumptionMetrics = (customHistory = null) => {
  const history = customHistory || getAiQueryHistory();
  const successfulQueries = history.filter((h) => h.success);

  const totalQueries = history.length;
  const totalInputTokens = successfulQueries.reduce((sum, h) => sum + (h.inputTokens || 0), 0);
  const totalOutputTokens = successfulQueries.reduce((sum, h) => sum + (h.outputTokens || 0), 0);
  const totalTokens = totalInputTokens + totalOutputTokens;
  const totalCostUsd = successfulQueries.reduce((sum, h) => sum + (h.costUsd || 0), 0);
  const totalCostCop = successfulQueries.reduce((sum, h) => sum + (h.costCop || 0), 0);

  return {
    totalQueries,
    successfulQueriesCount: successfulQueries.length,
    failedQueriesCount: totalQueries - successfulQueries.length,
    totalInputTokens,
    totalOutputTokens,
    totalTokens,
    totalCostUsd,
    totalCostCop,
    avgTokensPerQuery: successfulQueries.length > 0 ? Math.round(totalTokens / successfulQueries.length) : 0,
    avgCostPerQueryUsd: successfulQueries.length > 0 ? totalCostUsd / successfulQueries.length : 0,
  };
};
